<template>
  <section id="loginForm">
    <div class="response-message" v-if="this.responseMessage">
      {{ this.message }}
    </div>

    <!-- Login Form -->
    <q-form
      v-if="!this.changePasswordForm && this.loginForm"
      @submit.prevent="signIn"
      @reset="goToForgotPasswordForm"
      class="q-gutter-sm the-login-form"
    >
      <q-input
        label="Email"
        name="email"
        label-color="black"
        v-model="form.email"
        type="email"
        lazy-rules
        outlined
        :rules="[(val) => (val && val.length > 0) || 'Please enter an email.']"
        ><template v-slot:prepend>
          <q-icon name="mail" color="grey-5" /> </template
      ></q-input>
      <q-input
        autocomplete="true"
        name="password"
        label="Password"
        label-color="black"
        :type="showPassword ? 'password' : 'text'"
        outlined
        v-model="form.password"
        lazy-rules
        :rules="[
          (val) => (val && val.length > 0) || 'Please enter a password.',
        ]"
      >
        <template v-slot:prepend>
          <q-icon name="lock" color="grey-5" />
        </template>
        <template v-slot:append>
          <q-icon
            :name="showPassword ? 'visibility_off' : 'visibility'"
            class="cursor-pointer"
            @click="showPassword = !showPassword"
            color="black"
          />
        </template>
      </q-input>

      <q-toggle
        name="rememberMe"
        v-model="rememberMe"
        label="Remember Me"
        @click="!rememberMe"
      />

      <div
        id="signInError"
        style="display: none; font-weight: bold; color: red"
      >
        Hello<br />
      </div>
      <div class="q-mt-md">
        <q-btn
          type="submit"
          class="login-button"
          name="button__signIn"
          unelevated
          no-caps
          color="secondary"
          >Sign In</q-btn
        ><q-btn
          label="Forgot Password"
          name="button__forgotPassword"
          type="reset"
          no-caps
          outline
          class="reset-button"
        />
      </div>
    </q-form>
    <!-- Change Password on First Login -->
    <q-form
      v-if="this.changePasswordForm"
      @submit.prevent="updatePasswordOnFirstSignIn"
      class="q-gutter-sm the-login-form"
    >
      <p>
        <strong>Please create a new password</strong><br />
        Password should contain a minimum of 8 characters and at least one each
        of the following; a lowercase letter, an uppercase letter, a number and
        a special character.
      </p>
      <q-input
        label="New Password"
        label-color="black"
        :type="showPassword ? 'password' : 'text'"
        outlined
        v-model="form.newPassword"
      >
        <template v-slot:prepend>
          <q-icon name="lock" color="grey-5" />
        </template>
        <template v-slot:append>
          <q-icon
            :name="showPassword ? 'visibility_off' : 'visibility'"
            class="cursor-pointer"
            @click="showPassword = !showPassword"
            color="black"
          />
        </template>
      </q-input>
      <q-input
        label="Confirm New Password"
        label-color="black"
        :type="showPassword ? 'password' : 'text'"
        outlined
        v-model="form.newPasswordConfirm"
        reactive-rules
        v-bind:rules="[
          (value) =>
            value == this.form.newPassword.substring(0, value.length) ||
            value === this.form.newPassword ||
            'Passwords must match.',
        ]"
      >
        <template v-slot:prepend>
          <q-icon name="lock" color="grey-5" />
        </template>
        <template v-slot:append>
          <q-icon
            :name="showPassword ? 'visibility_off' : 'visibility'"
            class="cursor-pointer"
            @click="showPassword = !showPassword"
            color="black"
          />
        </template>
      </q-input>
      <div
        id="newPasswordError"
        style="display: none; font-weight: bold; color: red"
      >
        Hello<br />
      </div>
      <div class="q-mt-md">
        <q-btn
          type="submit"
          class="login-button"
          unelevated
          no-caps
          color="secondary"
          >Confirm</q-btn
        >
      </div>
    </q-form>
    <!-- Forgot Password - 1. Send Confirmation Email -->
    <q-form
      v-if="this.forgotPasswordEmailForm"
      @submit.prevent="submitForgotPasswordEmail"
      @reset="backToSignIn"
      class="q-gutter-sm the-login-form"
    >
      <p>
        <strong>Forgot Password</strong><br />
        You can reset your password below by entering in your email address. A
        verfication code will then be sent to your inbox.
      </p>
      <q-input
        label="Email"
        label-color="black"
        v-model="form.email"
        type="email"
        lazy-rules
        outlined
        :rules="[(val) => (val && val.length > 0) || 'Please enter an email.']"
        ><template v-slot:prepend>
          <q-icon name="mail" color="grey-5" /> </template
      ></q-input>
      <div
        id="resetViaEmailError"
        style="display: none; font-weight: bold; color: red"
      >
        Hello<br />
      </div>
      <div class="q-mt-md">
        <q-btn
          type="submit"
          class="login-button"
          unelevated
          no-caps
          color="secondary"
          >Confirm</q-btn
        >
        <q-btn
          label="Go Back"
          type="reset"
          color="secondary"
          no-caps
          outline
          class="reset-button"
        />
      </div>
    </q-form>
    <!-- Forgot Password - 2. Enter Verification Code -->
    <q-form
      v-if="this.forgotPasswordVerificationForm"
      @submit.prevent="submitForgotPasswordVerification"
      @reset="backToSignIn"
      class="q-gutter-sm the-login-form"
    >
      <p>
        <strong>Enter Verification Code</strong><br />
        A verification code should have been sent to your inbox. Please check
        and enter the number below.
      </p>
      <q-input
        label="Verfication Code"
        label-color="black"
        v-model="form.code"
        type="text"
        mask="######"
        debounce="1000"
        lazy-rules
        outlined
        :rules="[
          (val) => (val && val.length > 0) || 'Please enter verification code.',
          (val) =>
            (val && val.length >= 6) || 'Verification code should be 6 digits.',
        ]"
        ><template v-slot:prepend>
          <q-icon name="security" color="grey-5" /> </template
      ></q-input>
      <div
        id="verificationCodeError"
        style="display: none; font-weight: bold; color: red"
      >
        Hello<br />
      </div>
      <div class="q-mt-md">
        <q-btn
          type="submit"
          class="login-button"
          unelevated
          no-caps
          color="secondary"
          >Confirm</q-btn
        >
        <q-btn
          label="Go Back"
          type="reset"
          color="secondary"
          no-caps
          outline
          class="reset-button"
        />
      </div>
    </q-form>
    <!-- Forgot Password - 3. Enter New Password -->
    <q-form
      v-if="this.forgotPasswordNewPasswordForm"
      @submit.prevent="sumbitForgotPasswordNewPassword"
      @reset="backToSignIn"
      class="q-gutter-sm the-login-form"
    >
      <p>
        <strong>Please create a new password</strong><br />
        Password should contain a minimum of 8 characters and at least one each
        of the following; a lowercase letter, an uppercase letter, a number and
        a special character.
      </p>

      <q-input
        label="New Password"
        label-color="black"
        :type="showPassword ? 'password' : 'text'"
        outlined
        v-model="form.newPassword"
        lazy-rules
        v-bind:rules="validatePassword"
      >
        <template v-slot:prepend>
          <q-icon name="lock" color="grey-5" />
        </template>
        <template v-slot:append>
          <q-icon
            :name="showPassword ? 'visibility_off' : 'visibility'"
            class="cursor-pointer"
            @click="showPassword = !showPassword"
            color="black"
          />
        </template>
      </q-input>
      <q-input
        label="Confirm New Password"
        label-color="black"
        :type="showPassword ? 'password' : 'text'"
        outlined
        v-model="form.newPasswordConfirm"
        lazy-rules
        v-bind:rules="[
          validatePassword,
          (value) =>
            (value && value === this.form.newPassword) ||
            'Passwords must match.',
        ]"
      >
        <template v-slot:prepend>
          <q-icon name="lock" color="grey-5" />
        </template>
        <template v-slot:append>
          <q-icon
            :name="showPassword ? 'visibility_off' : 'visibility'"
            class="cursor-pointer"
            @click="showPassword = !showPassword"
            color="black"
          />
        </template>
      </q-input>
      <div
        id="forgottenNewPasswordError"
        style="display: none; font-weight: bold; color: red"
      >
        Hello<br />
      </div>
      <div class="q-mt-md">
        <q-btn
          type="submit"
          class="login-button"
          unelevated
          no-caps
          color="secondary"
          >Confirm</q-btn
        >
        <q-btn
          label="Go Back"
          type="reset"
          color="secondary"
          no-caps
          outline
          class="reset-button"
        />
      </div>
    </q-form>
  </section>
</template>

<script>
import { Auth } from "aws-amplify";
export default {
  name: "TheLoginForm",
  data() {
    return {
      signedIn: false,
      showPassword: true,
      loginForm: true,
      changePasswordForm: false,
      forgotPasswordEmailForm: false,
      forgotPasswordVerificationForm: false,
      forgotPasswordNewPasswordForm: false,
      forgotPasswordSuccessMessage: false,
      form: {
        email: "",
        password: "",
        newPassword: "",
        newPasswordConfirm: "",
        code: "",
      },
      responseMessage: false,
      message: "",
      rememberMe: false,
    };
  },
  beforeCreate() {
    Auth.currentAuthenticatedUser()
      .then(() => {
        this.signedIn = true;
      })
      .catch(() => {
        this.signedIn = false;
      });
  },

  computed: {
    validatePassword() {
      return [
        (value) =>
          (value && value.length >= 8) ||
          "Password must be 8 or more characters.",
        (value) =>
          (value && value.match(/[a-z]/g)) ||
          "Must contain one lowercase character.",
        (value) =>
          (value && value.match(/[A-Z]/g)) ||
          "Must contain one uppercase character.",
        (value) =>
          (value && value.match(/[0-9]/g)) || "Must contain one number.",
        (value) =>
          (value && value.match(/[!@#$%^&*]/g)) ||
          "Must contain one special character.",
      ];
    },
  },

  methods: {
    signIn() {
      const { email, password } = this.form;
      // Store reference to avoid context loss in async operations
      const store = this.$store;

      Auth.signIn(email, password)
        .then(async (user) => {
          document.getElementById("signInError").style.display = "none";
          if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
            this.changePasswordForm = true;
          } else {
            // Add defensive check before committing
            if (!store) {
              console.error("Store is undefined during sign in - QA environment issue");
              return;
            }
            store.commit("SET_USERNAME_AND_TOKEN", {
              username: email,
              jwtToken: user.signInUserSession.accessToken.jwtToken,
            });

            // Push coginto id to the dataLayer (Auth.user.username)
            window.dataLayer.push({
              event: "Login",
              status: "Success",
              loginType: "Returning user",
              cognitoId: Auth.user.username,
            });
          }
        })
        .catch((error) => {
          setErrorMessageVisible(
            document.getElementById("signInError"),
            error.message
          );
          // Push coginto id to the dataLayer (Auth.user.username)
          window.dataLayer.push({
            event: "Login",
            status: "Failure",
            loginType: "Returning user",
            cognitoId: Auth.user.username,
          });
          console.log(error);
        });

      //remember me
      if (this.rememberMe) localStorage.setItem("cpUser", email);
      else localStorage.removeItem("cpUser");
    },

    updatePasswordOnFirstSignIn() {
      const { email, password } = this.form;
      const newPassword = this.form.newPasswordConfirm;
      // Store reference to avoid context loss in async operations
      const store = this.$store;

      document.getElementById("newPasswordError").style.display = "None";
      Auth.signIn(email, password)
        .then((user) => {
          if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
            Auth.completeNewPassword(user, newPassword)
              .then(() => {
                // Add defensive check before committing
                if (!store) {
                  console.error("Store is undefined during password update - QA environment issue");
                  return;
                }
                store.commit("SET_USERNAME_AND_TOKEN", {
                  username: email,
                  jwtToken: user.signInUserSession.accessToken.jwtToken,
                });
                // Push coginto id to the dataLayer (Auth.user.username)
                window.dataLayer.push({
                  event: "Login",
                  status: "Success",
                  loginType: "First time login",
                  cognitoId: Auth.user.username,
                });
              })
              .catch((error) => {
                setErrorMessageVisible(
                  document.getElementById("newPasswordError"),
                  error.message
                );
                // Push coginto id to the dataLayer (Auth.user.username)
                window.dataLayer.push({
                  event: "Login",
                  status: "Failure",
                  loginType: "First time login",
                  cognitoId: Auth.user.username,
                });
                console.log(error);
              });
          }
        })
        .catch((error) => {
          setErrorMessageVisible(
            document.getElementById("newPasswordError"),
            error.message
          );
          console.log(error);
        });
    },

    goToForgotPasswordForm() {
      this.loginForm = false;
      this.forgotPasswordEmailForm = true;
    },

    submitForgotPasswordEmail() {
      const { email } = this.form;
      Auth.forgotPassword(email)
        .then(() => {
          this.forgotPasswordEmailForm = false;
          this.forgotPasswordVerificationForm = true;
        })
        .catch((error) => {
          setErrorMessageVisible(
            document.getElementById("resetViaEmailError"),
            error.message
          );
          console.log(error);
        });
    },

    submitForgotPasswordVerification() {
      this.forgotPasswordVerificationForm = false;
      this.forgotPasswordNewPasswordForm = true;
    },

    sumbitForgotPasswordNewPassword() {
      const { email, code, newPassword } = this.form;
      // Store reference to avoid context loss in async operations
      const store = this.$store;

      Auth.forgotPasswordSubmit(email, code, newPassword)
        .then(() => {
          // Add defensive check before committing
          if (!store) {
            console.error("Store is undefined during password reset - QA environment issue");
            return;
          }
          store.commit("SET_USERNAME_AND_TOKEN", {
            username: email,
          });
          Auth.signIn(email, newPassword).then((user) => {
            // Add defensive check before committing
            if (!store) {
              console.error("Store is undefined during sign in after password reset - QA environment issue");
              return;
            }
            store.commit("SET_USERNAME_AND_TOKEN", {
              username: email,
              jwtToken: user.signInUserSession.accessToken.jwtToken,
            });
          });
          // Push coginto id to the dataLayer (Auth.user.username)
          window.dataLayer.push({
            event: "Login",
            status: "Success",
            loginType: "Forgot Password Reset",
            cognitoId: Auth.user.username,
          });
        })
        .catch((error) => {
          setErrorMessageVisible(
            document.getElementById("forgottenNewPasswordError"),
            error.message
          );
          console.log(error);
        });
    },

    backToSignIn() {
      this.loginForm = true;
      this.forgotPasswordEmailForm = false;
      this.forgotPasswordVerificationForm = false;
      this.forgotPasswordNewPasswordForm = false;
      this.form.email = "";
      this.form.password = "";
      this.form.newPassword = "";
      this.form.newPasswordConfirm = "";
      this.form.code = "";
    },
  },

  mounted() {
    const rememberMeEmail = localStorage.getItem("cpUser");

    if (rememberMeEmail != null) {
      this.form.email = rememberMeEmail;
      this.rememberMe = true;
    } else {
      this.rememberMe = false;
    }
  },
};

function setErrorMessageVisible(errorMessageContainer, errorMessage) {
  errorMessageContainer.innerHTML = errorMessage;
  errorMessageContainer.style.display = "inline";
}
</script>

<style lang="scss">
.the-login-form {
  font-family: Barlow;
  font-style: normal;
  .login-button,
  .reset-button {
    height: 50px;
    width: 100%;
    text-align: center;
    border-radius: 10px;
    font-size: 14px;
  }
  .login-button {
    margin-bottom: 10px;
  }
}
.response-message {
  padding: 8px 12px;
  margin: 0 0 30px;
  border-radius: 4px;
  background: #e0e0e0;
}
</style>
